import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Box, Chip, FormControl, FormLabel, Stack } from '@mui/material';
import { FormComponentBuilder } from 'components/common/form/layout';
import {
    FormComponentValue,
    FormControlProps,
    FormElementControlPropsType,
} from 'components/common/form/layout/control';
import { FormElementProps } from 'components/common/form/index';
import IconHandler from 'components/common/icon/IconHandler';
import ReactSelect from 'components/common/react-select/ReactSelect';
import useBundleTranslation from 'i18n';
import { StaticAddon } from 'components/common/static-addon/StaticAddon';

interface InsightResourcePickerProps extends FormControlProps {
    data: Array<FormComponentValue>;
    elementProps: FormElementProps;
}

const ALL_TYPES = '__all__';

export default function InsightResourcePicker({
    controlProps,
}: FormElementControlPropsType<InsightResourcePickerProps>) {
    const { t } = useBundleTranslation('app/editor/element/extreport');
    const value: string[] = useMemo(() => {
        if (Array.isArray(controlProps.value)) return controlProps.value.map(String);
        if (controlProps.value) return [String(controlProps.value)];
        return [];
    }, [controlProps.value]);

    const { primaryOptions, additionalOptions } = useMemo(
        () => ({
            primaryOptions: controlProps.data.filter((opt) => opt.props?.is_primary),
            additionalOptions: controlProps.data.filter((opt) => !opt.props?.is_primary),
        }),
        [controlProps.data],
    );

    const primaryKeys = useMemo(() => primaryOptions.map((opt) => String(opt.value)), [primaryOptions]);

    const additionalValue = useMemo(() => value.filter((v) => !primaryKeys.includes(v)), [value, primaryKeys]);

    const [typeFilter, setTypeFilter] = useState(ALL_TYPES);

    // Counts (and the filter itself) only ever consider resources not yet added —
    // already-added chips must stay visible regardless of the type filter.
    const typeCounts = useMemo(() => {
        const counts = new Map<string, { label: string; count: number }>();
        additionalOptions.forEach((opt) => {
            if (additionalValue.includes(String(opt.value))) return;
            const logoKey = String(opt.props?.logo_key ?? '');
            if (!logoKey) return;
            const label = String(opt.props?.type_label ?? logoKey);
            counts.set(logoKey, { label, count: (counts.get(logoKey)?.count ?? 0) + 1 });
        });
        return counts;
    }, [additionalOptions, additionalValue]);

    const typeFilterOptions: Array<FormComponentValue> = useMemo(
        () => [
            { value: ALL_TYPES, label: t('concierge_insight_filter_all_types') },
            ...Array.from(typeCounts.entries()).map(([logoKey, { label, count }]) => ({
                value: logoKey,
                label: `${label} (${count})`,
            })),
        ],
        [typeCounts, t],
    );

    // A filtered-to type with nothing left to add (everything of that type got added) —
    // fall back to All types instead of showing an empty menu tied to a vanished option.
    useLayoutEffect(() => {
        if (typeFilter !== ALL_TYPES && !typeCounts.has(typeFilter)) {
            setTypeFilter(ALL_TYPES);
        }
    }, [typeCounts, typeFilter]);

    const pickerOptions = useMemo(
        () =>
            typeFilter === ALL_TYPES
                ? additionalOptions
                : additionalOptions.filter(
                      (opt) => additionalValue.includes(String(opt.value)) || opt.props?.logo_key === typeFilter,
                  ),
        [additionalOptions, additionalValue, typeFilter],
    );

    // Ensure primary keys are always present in the form value.
    useEffect(() => {
        if (primaryKeys.length === 0) return;
        const missing = primaryKeys.filter((k) => !value.includes(k));
        if (missing.length > 0) {
            controlProps.onChange([...value, ...missing]);
        }
    }, [primaryKeys]);

    const handleAdditionalChange = (newVal: string | string[]) => {
        const additional = Array.isArray(newVal) ? newVal : newVal ? [newVal] : [];
        controlProps.onChange([...primaryKeys, ...additional]);
    };

    return (
        <Stack spacing={0.5}>
            <FormLabel sx={{ fontWeight: 600 }}>{t('concierge_insight_data_resources')}</FormLabel>
            {primaryOptions.length > 0 && (
                <FormControl>
                    <FormLabel>{t('concierge_insight_primary_chip')}</FormLabel>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {primaryOptions.map((opt) => (
                            <Chip
                                key={String(opt.value)}
                                icon={opt.props?.icon ? <IconHandler icon={opt.props.icon} /> : undefined}
                                label={opt.label}
                            />
                        ))}
                    </Stack>
                </FormControl>
            )}
            {additionalOptions.length > 0 && (
                <FormControl sx={{ pt: 1.5 }}>
                    <FormLabel>{t('concierge_insight_additional_chip')}</FormLabel>
                    <Stack direction="row" sx={{ mb: 2, width: '50%' }}>
                        <StaticAddon>{t('concierge_insight_filter_by_type')}</StaticAddon>
                        <Box sx={{ flexGrow: 1 }}>
                            <ReactSelect
                                data={typeFilterOptions}
                                value={typeFilter}
                                update={(newVal) => setTypeFilter((newVal as string) || ALL_TYPES)}
                                name={`${controlProps.name}_type_filter`}
                                isMulti={false}
                                isClearable={false}
                                disabled={controlProps.readOnly}
                                dataTest={`${controlProps.name}_type_filter`}
                            />
                        </Box>
                    </Stack>
                    <ReactSelect
                        data={pickerOptions}
                        value={additionalValue}
                        update={handleAdditionalChange}
                        name={controlProps.name}
                        isMulti={true}
                        disabled={controlProps.readOnly}
                        dataTest={controlProps.name}
                        placeholder={t('concierge_insight_resource_search_ph')}
                    />
                </FormControl>
            )}
        </Stack>
    );
}

export class InsightResourcePickerBuilder extends FormComponentBuilder {
    processFormData(formData: FormData, key: string, data: any) {
        if (!Array.isArray(data)) {
            formData.append(key, data);
        } else {
            data.forEach((v: string) => formData.append(key + '[]', v));
        }
    }

    prepareProps(): InsightResourcePickerProps {
        return {
            ...this.controlProps,
            data: this.componentValues,
            elementProps: this.elementProps,
        };
    }
}
